import { useState, type ReactNode } from 'react';
import { AlertCircle } from 'lucide-react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Separator,
} from '@predoc/cow';
import { useCancelPatientOpenRequests } from '../../hooks/patientsAPI';

type CancelOpenRequestsDialogProps = {
  patientId: string;
  children: ReactNode;
};

export const CancelOpenRequestsDialog = ({
  patientId,
  children,
}: CancelOpenRequestsDialogProps) => {
  const cancelOpenRequestsMutation = useCancelPatientOpenRequests();
  const [open, setOpen] = useState(false);

  const handleConfirmCancel = () => {
    if (cancelOpenRequestsMutation.isPending) return;

    cancelOpenRequestsMutation.mutate(
      { patientId },
      {
        onSuccess: () => setOpen(false),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="p-0" aria-describedby={undefined}>
        <DialogHeader className="p-4 pb-0">
          <DialogTitle>Cancel Open Requests</DialogTitle>
        </DialogHeader>
        <Separator />
        <div className="p-4">
          <Alert variant="destructive">
            <AlertCircle className="size-5" />
            <AlertTitle>Confirm Cancel Open Requests</AlertTitle>
            <AlertDescription>
              You&apos;re about to request cancellation of open requests.
              Requests that are currently processing cannot be canceled but
              every effort will be made to cancel before processing begins.
            </AlertDescription>
          </Alert>
        </div>
        <Separator />
        <DialogFooter className="flex justify-end p-2 pt-0">
          <Button variant="outline" onClick={() => setOpen(false)}>
            Back
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirmCancel}
            disabled={cancelOpenRequestsMutation.isPending}
          >
            Cancel Open Requests
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
